import React, { useEffect, useState } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import { useLazyGetDashboardPayableAmountQuery } from "store/api/reports/reports"
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
} from "reactstrap"
import downArrowIcon from "../../../assets/images/icons/down-arrow-color.svg"
import Sliders from "../../../assets/images/icons/sliders.svg"
import { formatAmount } from "helpers/common"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const periods = [
  "This Year",
  "This Half Year",
  "This Quarter",
  "This Month",
  "This Week",
]

const AmountPayableChart = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)
  const toggle = () => setDropdownOpen((prevState) => !prevState)
  const [selected, setSelected] = useState(["Interest", "Principal"])
  const [filters, setFilters] = useState({ period: "This Year" })
  const [tooltips, setTooltips] = useState([])

  const [data, setData] = useState(null)
  const [maxAbs, setMaxAbs] = useState(0)
  const [originalData, setOriginalData] = useState([])
  const [principalAmount, setPrincipalAmount] = useState(0)
  const [interestAmount, setInterestAmount] = useState(0)

  const [getResults, { isFetching }] = useLazyGetDashboardPayableAmountQuery()

  const getList = async () => {
    try {
      const { data } = await getResults({ ...filters }).unwrap()
      setInterestAmount(
        data?.reduce((sum, item) => sum + (item.Interest || 0), 0) || 0
      )
      setPrincipalAmount(
        data?.reduce((sum, item) => sum + (item.Principal || 0), 0) || 0
      )
      const values = [
        ...data.map((item) => item.Principal),
        ...data.map((item) => item.Interest),
      ]
      const abs = Math.max(...values.map((v) => Math.abs(v))) * 1.2
      setMaxAbs(abs)
      setOriginalData(data)
    } catch (e) {}
  }

  const updateData = () => {
    setData({
      labels: originalData.map((item) => item.label),
      datasets: [
        ...(selected.includes("Principal")
          ? [
              {
                label: "Principal",
                data: originalData
                  .map((item) => item.Principal)
                  .map((v) => (v === 0 ? null : -v)),
                backgroundColor: "#F5F5F5",
                hoverBackgroundColor: "#88BBEA",
                stack: "combined",
                borderRadius: 6,
                minBarLength: 20,
              },
            ]
          : []),
        ...(selected.includes("Interest")
          ? [
              {
                label: "Interest",
                data: originalData
                  .map((item) => item.Interest)
                  .map((v) => (v === 0 ? null : v)),
                backgroundColor: "#E0E0E0",
                hoverBackgroundColor: "#0B5798",
                stack: "combined",
                borderRadius: 6,
                minBarLength: 20,
              },
            ]
          : []),
      ],
    })
  }

  useEffect(() => {
    if (originalData.length > 0) {
      updateData()
    }
  }, [selected, originalData])

  useEffect(() => {
    getList()
  }, [filters])

  const handleChange = (item) => {
    setSelected((prevSelected) =>
      prevSelected.includes(item)
        ? prevSelected.filter((i) => i !== item)
        : [...prevSelected, item]
    )
  }

  const updateTooltips = (newTips) => {
    setTooltips((prev) => {
      const same =
        prev.length === newTips.length &&
        prev.every(
          (p, i) =>
            p.label === newTips[i].label &&
            p.value === newTips[i].value &&
            p.x === newTips[i].x &&
            p.y === newTips[i].y
        )

      return same ? prev : newTips
    })
  }

  const options = {
    responsive: true,
    animation: false,
    hover: { animationDuration: 0 },
    interaction: { mode: "index", intersect: false },

    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (context) => {
          const chart = context.chart
          const active = chart.getActiveElements()

          if (!active.length) {
            updateTooltips([])
            return
          }

          const index = active[0].index

          const tips = chart.data.datasets.map((ds, i) => {
            const value = ds.data[index]

            if (value === null) return null

            const bar = chart.getDatasetMeta(i).data[index]
            if (!bar) return null

            const pos = bar.tooltipPosition()

            return {
              label: ds.label,
              value: "₹" + Math.abs(value).toLocaleString(),
              x: pos.x,
              y: pos.y - 20,
              className:
                ds.label === "Interest"
                  ? "tooltip-interest"
                  : "tooltip-principal",
            }
          })

          updateTooltips(tips.filter(Boolean))
        },
      },
    },

    scales: {
      x: { stacked: true, grid: { display: false } },
      y: {
        stacked: true,
        ticks: { display: false },
        border: { display: false },
        min: -maxAbs,
        max: maxAbs,
        grid: { color: "#eee" },
      },
    },
  }

  return (
    <>
      <div className="chart-top-section-wrapper">
        <h2>Amount Payable</h2>
        <div className="tab-section">
          {selected.includes("Interest") && (
            <p>
              Interest : <span>₹ {formatAmount(interestAmount)}</span>
            </p>
          )}
          {selected.includes("Principal") && (
            <p className="principal">
              Principal : <span>₹ {formatAmount(principalAmount)}</span>
            </p>
          )}
        </div>

        <div className="dropdown-section">
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle caret size="lg">
              {filters.period} <img src={downArrowIcon} alt="" />
            </DropdownToggle>
            <DropdownMenu
              style={{
                minWidth: "auto",
              }}
            >
              {periods.map((item, index) => (
                <DropdownItem
                  key={`periods_${index}`}
                  onClick={() => setFilters({ period: item })}
                >
                  {item}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>

          <div className="filter-section">
            <Dropdown
              isOpen={typeDropdownOpen}
              toggle={() => setTypeDropdownOpen(!typeDropdownOpen)}
            >
              <DropdownToggle size="lg">
                <img src={Sliders} alt="" style={{ marginLeft: 0 }} />
              </DropdownToggle>
              <DropdownMenu>
                <div className="dropdown-item-section">
                  <div className="filter-reset" style={{ margin: 0 }}>
                    <p style={{ textDecoration: "none", cursor: "unset" }}>
                      Select Type
                    </p>
                  </div>
                  <div className="checkbox-section">
                    {["Interest", "Principal"].map((item, index) => (
                      <FormGroup check key={`types_${index}`}>
                        <Input
                          type="checkbox"
                          id={item}
                          checked={selected.includes(item)}
                          value={item}
                          onChange={() => handleChange(item)}
                        />
                        <Label htmlFor={item} check>
                          {item}
                        </Label>
                      </FormGroup>
                    ))}
                  </div>
                </div>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="label-section-wrapper">
        <div className="interest">
          <span /> <p>Interest</p>
        </div>
        <div className="principal">
          <span /> <p>Principal</p>
        </div>
      </div>
      <div style={{ position: "relative", width: "100%" }}>
        {data && <Bar data={data} options={options} />}

        {tooltips.map((t, idx) => (
          <div
            key={idx}
            className={`tooltip-custom ${t.className}`}
            style={{
              position: "absolute",
              left: t.x,
              top: t.y,
              transform: "translate(-50%, -100%)",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            <div className="tooltip-label">{t.label}</div>
            <div className="tooltip-value">{t.value}</div>
          </div>
        ))}
      </div>
    </>
  )
}

export default AmountPayableChart
